import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';

/** How often to check for a new version while the portal stays open. */
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * add-parent-portal-pwa 2.1: watches for new deployed versions via the Angular
 * service worker and exposes a signal the app shell uses to offer a reload.
 * A parent may leave the portal open, so this also polls periodically rather
 * than relying only on the browser's own update checks (which run on
 * navigation/registration).
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);

  readonly updateAvailable = signal(false);

  constructor() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    const subscription = this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      if (event.type === 'VERSION_READY') {
        this.updateAvailable.set(true);
      }
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());

    const intervalId = setInterval(() => void this.swUpdate.checkForUpdate(), CHECK_INTERVAL_MS);
    this.destroyRef.onDestroy(() => clearInterval(intervalId));
  }

  /** Activates the pending version and reloads to run it. */
  async reloadForUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      await this.swUpdate.activateUpdate();
    }
    this.reloadPage();
  }

  /** Isolated for testability — jsdom doesn't allow stubbing `window.location` directly. */
  protected reloadPage(): void {
    window.location.reload();
  }
}
